import { z } from 'zod'

// =====================
// Status Enums
// =====================

export const ExternalOrgStatuses = [
  'PENDING',
  'ACTIVE',
  'SUSPENDED',
  'REVOKED',
] as const

export type ExternalOrgStatus = (typeof ExternalOrgStatuses)[number]

export const ExternalOrgInviteStatuses = [
  'PENDING',
  'ACCEPTED',
  'DECLINED',
  'EXPIRED',
  'CANCELLED',
] as const

export type ExternalOrgInviteStatus = (typeof ExternalOrgInviteStatuses)[number]

export const ExternalOrgMemberStatuses = [
  'ACTIVE',
  'SUSPENDED',
  'REVOKED',
] as const

export type ExternalOrgMemberStatus = (typeof ExternalOrgMemberStatuses)[number]

export const ExternalOrgInviteDirections = [
  'HOST_TO_PARTNER',
  'PARTNER_TO_HOST',
] as const

export type ExternalOrgInviteDirection =
  (typeof ExternalOrgInviteDirections)[number]

// =====================
// Organization Reference Schema (for relations)
// =====================

const OrganizationRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  logo: z.string().nullable().optional(),
  slug: z.string(),
})

const UserRefSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
})

// Permission definition schema for external org access
const PermissionDefinitionRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  moduleType: z.string(),
  allowedActions: z.array(z.string()),
})

// Permission set item schema
const PermissionSetItemRefSchema = z.object({
  id: z.string().uuid().optional(),
  permissionSetId: z.string().uuid(),
  permissionDefinitionId: z.string().uuid(),
  permissionDefinition: PermissionDefinitionRefSchema.optional(),
})

const PermissionSetRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  // Extended permission set items with definitions for external org access checking
  permissionSetItems: z.array(PermissionSetItemRefSchema).optional(),
})

const MemberRefSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  user: UserRefSchema.optional(),
})

// =====================
// External Org Relationship
// =====================

export const ExternalOrgRelationshipSchema = z.object({
  id: z.string().uuid(),
  hostOrganizationId: z.string().uuid(),
  partnerOrganizationId: z.string().uuid(),
  status: z.enum(ExternalOrgStatuses),
  permissionSetId: z.string().uuid().nullable().optional(),
  invitedBy: z.string().uuid(),
  acceptedBy: z.string().uuid().nullable().optional(),
  showInAssignees: z.boolean().default(false),
  metadata: z.any().default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  revokedAt: z.date().nullable().optional(),

  // Relations
  hostOrganization: OrganizationRefSchema.optional(),
  partnerOrganization: OrganizationRefSchema.optional(),
  permissionSet: PermissionSetRefSchema.nullable().optional(),
  inviter: UserRefSchema.optional(),
  accepter: UserRefSchema.nullable().optional(),
  // Note: externalMembers is optional and loaded separately to avoid circular refs
})

export type ExternalOrgRelationship = z.infer<
  typeof ExternalOrgRelationshipSchema
>

// =====================
// External Org Invite
// =====================

export const ExternalOrgInviteSchema = z.object({
  id: z.string().uuid(),
  direction: z.enum(ExternalOrgInviteDirections).default('HOST_TO_PARTNER'),
  hostOrganizationId: z.string().uuid().nullable().optional(),
  partnerOrganizationId: z.string().uuid().nullable().optional(),
  partnerEmail: z.string().email().nullable().optional(),
  partnerOrgName: z.string().nullable().optional(),
  recipientEmail: z.string().email().nullable().optional(),
  token: z.string().optional(), // Hidden from responses
  status: z.enum(ExternalOrgInviteStatuses).default('PENDING'),
  permissionSetId: z.string().uuid().nullable().optional(),
  invitedBy: z.string().uuid(),
  message: z.string().nullable().optional(),
  showInAssignees: z.boolean().default(false),
  expiresAt: z.date(),
  createdAt: z.date(),
  updatedAt: z.date(),

  // Relations
  hostOrganization: OrganizationRefSchema.nullable().optional(),
  partnerOrganization: OrganizationRefSchema.nullable().optional(),
  permissionSet: PermissionSetRefSchema.nullable().optional(),
  inviter: UserRefSchema.optional(),
})

export type ExternalOrgInvite = z.infer<typeof ExternalOrgInviteSchema>

// =====================
// External Org Member
// =====================

export const ExternalOrgMemberSchema = z.object({
  id: z.string().uuid(),
  relationshipId: z.string().uuid(),
  memberId: z.string().uuid(),
  status: z.enum(ExternalOrgMemberStatuses).default('ACTIVE'),
  grantedBy: z.string().uuid(),
  lastAccessedAt: z.date().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  revokedAt: z.date().nullable().optional(),

  // Relations
  member: MemberRefSchema.optional(),
  grantor: UserRefSchema.optional(),
  // Note: relationship is loaded separately to avoid circular refs
})

export type ExternalOrgMember = z.infer<typeof ExternalOrgMemberSchema>

// =====================
// DTOs - Create External Org Invite
// =====================

export const CreateExternalOrgInviteSchema = z.object({
  partnerEmail: z.string().email('Invalid email address'),
  partnerOrgName: z.string().max(255).nullable().optional(),
  permissionSetId: z.string().uuid().nullable().optional(),
  message: z.string().max(500).nullable().optional(),
  showInAssignees: z.boolean().default(false),
})

export type CreateExternalOrgInviteDTO = z.infer<
  typeof CreateExternalOrgInviteSchema
>

// =====================
// DTOs - Accept/Decline Invite
// =====================

export const AcceptExternalOrgInviteSchema = z.object({
  token: z.string(),
  partnerOrganizationId: z.string().uuid(), // Which org to accept as
})

export type AcceptExternalOrgInviteDTO = z.infer<
  typeof AcceptExternalOrgInviteSchema
>

export const DeclineExternalOrgInviteSchema = z.object({
  token: z.string(),
})

export type DeclineExternalOrgInviteDTO = z.infer<
  typeof DeclineExternalOrgInviteSchema
>

// =====================
// DTOs - Partner-Initiated Support Requests
// =====================

// Sent by a partner org to request management/support access to a host org.
// The host admin receives the email and picks a permission set on accept.
export const CreateExternalOrgSupportRequestSchema = z.object({
  recipientEmail: z.string().email('Invalid email address'),
  hostOrgName: z.string().max(255).nullable().optional(),
  message: z.string().max(500).nullable().optional(),
})

export type CreateExternalOrgSupportRequestDTO = z.infer<
  typeof CreateExternalOrgSupportRequestSchema
>

export const AcceptExternalOrgSupportRequestSchema = z.object({
  token: z.string(),
  hostOrganizationId: z.string().uuid(),
  permissionSetId: z.string().uuid(),
  showInAssignees: z.boolean().optional(),
})

export type AcceptExternalOrgSupportRequestDTO = z.infer<
  typeof AcceptExternalOrgSupportRequestSchema
>

// =====================
// DTOs - Update Relationship
// =====================

export const UpdateExternalOrgRelationshipSchema = z.object({
  permissionSetId: z.string().uuid().nullable().optional(),
  showInAssignees: z.boolean().optional(),
})

export type UpdateExternalOrgRelationshipDTO = z.infer<
  typeof UpdateExternalOrgRelationshipSchema
>

// =====================
// DTOs - Grant/Revoke Member Access
// =====================

export const GrantExternalMemberAccessSchema = z.object({
  memberIds: z.array(z.string().uuid()).min(1),
})

export type GrantExternalMemberAccessDTO = z.infer<
  typeof GrantExternalMemberAccessSchema
>

export const RevokeExternalMemberAccessSchema = z.object({
  memberIds: z.array(z.string().uuid()).min(1),
})

export type RevokeExternalMemberAccessDTO = z.infer<
  typeof RevokeExternalMemberAccessSchema
>

// =====================
// Response Types
// =====================

export interface ExternalOrgInviteResponse {
  success: boolean
  invite?: ExternalOrgInvite
  error?: string
}

export interface ExternalOrgRelationshipResponse {
  success: boolean
  relationship?: ExternalOrgRelationship
  error?: string
}

export interface ExternalMemberAccessResponse {
  success: boolean
  granted: number
  failed: number
  errors?: Array<{
    memberId: string
    error: string
  }>
}
